import { css } from "@emotion/react";
import { omit } from "lodash/fp";
import { Fragment, useContext, useState } from "react";

import {
  Icon,
  Heading,
  useThemeProps,
  TypographyBlock,
  MixedGridLayout,
  Section,
  generateUniqueId,
} from "../../wsui/base";

import PostsModuleArchiveLink from "./PostsModuleArchiveLink.jsx";
import postsModuleContext from "./postsModuleContext";
import PostsModuleItem from "./PostsModuleItem.jsx";

export default function PostsModuleMixedLayout(props) {
  props = useThemeProps({ props, name: "MunicipioPostsModuleMixedLayout" });
  let {
    title,
    titleIcon,
    titleIconProps = { size: 8 },
    description,
    items,
    collapsedItemProps = {},
    columnGap = [4, 8],
    rowGap = 4,
    headingVariant,
    archiveLink,
    archiveLinkProps,
    headingId,
    align,
    ...restProps
  } = omit(["gridMaxColumns"], props);
  const contentAlign =
    align === "left" || align === "center" || align === "right"
      ? align
      : undefined;
  const archiveAlign =
    contentAlign === "right"
      ? "flex-end"
      : contentAlign === "center"
        ? "center"
        : "flex-start";

  let ownerState = useContext(postsModuleContext);
  let { itemVariant = "card", visibleFields } = ownerState;

  collapsedItemProps = {
    ...collapsedItemProps,
    variant: itemVariant,
    visibleFields: [...visibleFields].filter(
      (item) => item !== "image" && item !== "description",
    ),
  };

  const [defaltHeadingId] = useState(
    () => `module-heading-${generateUniqueId()}`,
  );
  headingId = headingId || defaltHeadingId;

  const MaybeSection = title ? Section : Fragment;

  return (
    <div
      css={css`
        height: 100%;
      `}
      {...restProps}
    >
      <div
        css={css`
          display: grid;
          grid-template-columns: 1fr max-content;
          align-items: start;
        `}
      >
        {title && (
          <Heading
            variant={headingVariant}
            marginAfter
            id={headingId}
            css={css`
              display: flex;
              gap: 0.5em;
              align-items: start;
            `}
          >
            {titleIcon && (
              <Icon
                name={titleIcon}
                css={css`
                  align-self: center;
                  flex: none;
                `}
                {...titleIconProps}
              />
            )}
            {title}
          </Heading>
        )}
        {archiveLink && (
          <PostsModuleArchiveLink
            align={archiveAlign}
            {...archiveLink}
            {...archiveLinkProps}
          />
        )}
      </div>
      {description && (
        <TypographyBlock marginAfter align="left">{description}</TypographyBlock>
      )}
      <MaybeSection>
        <MixedGridLayout
          columnGap={columnGap}
          rowGap={rowGap}
          items={items}
          renderItem={(item, itemMeta) => {
            let { columnItemCount, itemIndex, columnCount } = itemMeta;
            let isExpanded = columnItemCount === 1 || itemIndex === 0;
            let itemProps = { visibleFields, variant: itemVariant };
            if (!isExpanded) {
              itemProps = { ...itemProps, ...collapsedItemProps };
            }
            return (
              <PostsModuleItem
                item={item}
                align={contentAlign}
                {...itemProps}
                css={css`
                  flex-grow: ${isExpanded ? 1 : null};
                  /* We get a weird layout error if we apply 100% height in one-column layout */
                  height: ${isExpanded && columnCount > 1 ? "100%" : null};
                `}
              />
            );
          }}
        />
      </MaybeSection>
    </div>
  );
}
